import { FC, type ReactNode } from 'react';
import classNames from 'classnames';
import {
  Button,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import { Add, Information, SubtractAlt } from '@carbon/react/icons';
import { SurveyAreaAutocomplete } from 'components/survey-area-autocomplete';
import { QuarterCalls } from 'components/quarter-calls';
import { emptySurveyRow, type SurveyRow } from './survey-rows';
import styles from './survey-section.module.scss';

interface SurveySectionProps {
  rows: SurveyRow[];
  onChange: (rows: SurveyRow[]) => void;
  /** Heading above the rows. */
  title?: string;
  /** Copy under the heading explaining how the rows combine. */
  hint?: string;
  /**
   * Prefix for the per-row input ids, so two of these on one page don't collide.
   */
  idPrefix?: string;
  /**
   * Extra control rendered inside each row. Optional so the shape of this
   * component is unchanged for every existing consumer — the Unit Builder uses
   * it for the mock's "Custom (use drawn shape)" checkbox.
   */
  renderRowExtra?: (row: SurveyRow) => ReactNode;
  /**
   * Hide the built-in bottom "Add survey area" button — for hosts (the Unit
   * Builder) that provide their own add control above the rows.
   */
  hideAddButton?: boolean;
  /**
   * Render the rows read-only: the survey search, quarter calls and the add /
   * remove controls are all disabled. For a host showing a survey description it
   * must not let the user edit — the Unit Builder scoped to an EXISTING unit,
   * whose tracts are already persisted. Defaults false, so every other consumer
   * is unchanged.
   */
  disabled?: boolean;
  /**
   * Minimum characters before the survey search hits the server. 0 (the default)
   * loads the first page as soon as a field is focused; the Unit Builder passes 2
   * so options are fetched only once the operator types. Every other consumer
   * takes the default and is unchanged.
   */
  searchMinChars?: number;
  /**
   * Label the columns once, on the first row, and stack the rest tighter — for a
   * narrow host (the area geometry editor's rail) where a label pair over every
   * row crowds the list. A later row's search spinner then sits beside its
   * field. Defaults false, so every other consumer is unchanged.
   */
  condensed?: boolean;
}

/**
 * A survey description: one row per survey area, each optionally carved down by
 * quarter calls. Rows are added and removed here; the map fills them in when a
 * host wires up picking.
 *
 * @param {SurveySectionProps} props - The component props.
 * @return {JSX.Element} The survey rows.
 */
export const SurveySection: FC<SurveySectionProps> = ({
  rows,
  onChange,
  title = 'Survey description',
  hint = 'Pick one or more survey areas (from the list or the map) and, optionally, a quarter call to carve down each tract. All rows combine into a single multi-polygon location.',
  idPrefix = 'survey',
  renderRowExtra,
  hideAddButton = false,
  disabled = false,
  searchMinChars = 0,
  condensed = false,
}) => {
  const patch = (key: string, next: Partial<SurveyRow>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...next } : row)));

  return (
    <section
      className={classNames(styles.section, {
        // Only rule off from what sits above when this section actually
        // introduces itself. A host that supplies neither a title nor a hint
        // (the Unit Builder) frames the rows with its own pane header, and the
        // divider's padding would just push the first row down.
        [styles.divided]: Boolean(title || hint),
      })}
    >
      {/* Rendered only when given. An empty heading still collapses to a 0px
          flex child, so the section's gap applied on both sides of it and left
          dead space above the first row. */}
      {title && <h4 className={styles.subsectionTitle}>{title}</h4>}
      {hint && <p className={styles.hint}>{hint}</p>}

      {/* Every row carries its own column labels: the survey field shows a
          loading spinner beside its label while that row's search runs, which
          a single header above the rows cannot attribute to one row. Condensed,
          only the first row is labelled and the others keep their spinner
          beside the field. */}
      <div
        className={classNames(styles.rows, { [styles.condensed]: condensed })}
      >
        {rows.map((row, index) => {
          const labelled = !condensed || index === 0;
          return (
            <div
              className={classNames(styles.surveyRow, {
                [styles.unlabelledRow]: !labelled,
              })}
              key={row.key}
            >
              <SurveyAreaAutocomplete
                id={`${idPrefix}-area-${row.key}`}
                placeholder="Search survey areas"
                {...(labelled
                  ? {}
                  : {
                      'titleText': '',
                      'aria-label': `Survey area ${index + 1}`,
                    })}
                index={index}
                location={{ value: row.location, state: 'stable' }}
                disabled={disabled}
                searchMinChars={searchMinChars}
                onChange={({ selectedItem }) =>
                  patch(row.key, { location: selectedItem ?? undefined })
                }
              />

              <QuarterCalls
                id={`${idPrefix}-call-${row.key}`}
                labelText={
                  <span className={styles.labelWithInfo}>
                    Quarter Call
                    <Toggletip align="top">
                      <ToggletipButton label="About quarter calls">
                        <Information />
                      </ToggletipButton>
                      <ToggletipContent>
                        <p>
                          Narrows this tract by legal description — S2 is the
                          south half, S2S2 the south quarter, SWNW the NW of the
                          SW. Type a call and press Enter, space or comma to add
                          it; calls stack.
                        </p>
                      </ToggletipContent>
                    </Toggletip>
                  </span>
                }
                hideLabel={!labelled}
                placeholder="Quarter Call"
                disabled={disabled}
                items={row.quarterCalls}
                onAdd={(call) =>
                  patch(row.key, {
                    quarterCalls: row.quarterCalls.includes(call)
                      ? row.quarterCalls
                      : [...row.quarterCalls, call],
                  })
                }
                onRemove={(call) =>
                  patch(row.key, {
                    quarterCalls: row.quarterCalls.filter((c) => c !== call),
                  })
                }
              />

              {renderRowExtra?.(row)}

              <Button
                className={styles.rowAction}
                kind="ghost"
                size="md"
                hasIconOnly
                iconDescription="Remove survey area"
                renderIcon={SubtractAlt}
                disabled={disabled}
                // Clearing the last row keeps one empty row rather than none at all.
                onClick={() =>
                  onChange(
                    rows.length === 1
                      ? [emptySurveyRow()]
                      : rows.filter((r) => r.key !== row.key)
                  )
                }
              />
            </div>
          );
        })}
      </div>

      {!hideAddButton && (
        <Button
          kind="ghost"
          size="md"
          renderIcon={Add}
          disabled={disabled}
          onClick={() => onChange([...rows, emptySurveyRow()])}
        >
          Add survey area
        </Button>
      )}
    </section>
  );
};

export default SurveySection;
